import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowRight, LogIn } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { apiFetch, setStoredToken } from '@/lib/api';
import { priceText, isConsultService } from './serviceMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GROUPS = [['support', 'Tech support & repair'], ['business', 'Business & specialized'], ['web', 'Web builds'], ['managed', 'Managed web services']];
const inputCls = 'w-full bg-[#0a0a1f] border border-[#00f0ff]/40 focus:border-[#00f0ff] focus:outline-none px-4 py-3 text-sm text-white placeholder:text-slate-500 rounded-none';
const labelCls = 'block font-tech text-xs uppercase tracking-widest text-[#00f0ff] mb-2';

function errorText(data) {
  if (typeof data?.detail === 'string') return data.detail;
  return 'Please check your name, email and details and try again.';
}

async function startSubscription(ticketId) {
  const res = await apiFetch(`${API}/payments/subscription/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ origin_url: window.location.origin, ticket_id: ticketId }),
  });
  if (res.ok) window.location.href = (await res.json()).url;
  return res.ok;
}

function ExistingAccount({ ticketId, onClose }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-5" data-testid="modal-existing-account">
      <p className="text-slate-200 text-sm leading-relaxed">
        Good news — you already have an account with us, so we've added this request to it and emailed you a link.
        Sign in to follow along and reply.
      </p>
      <button onClick={() => navigate(`/login?next=/tickets/${ticketId}`)} data-testid="modal-sign-in-btn"
        className="nw-btn w-full flex items-center justify-center gap-2 py-3 bg-[#00f0ff] text-[#0a0a1f] font-tech uppercase tracking-wider text-sm">
        <LogIn size={16} /> Sign in to continue
      </button>
      <button onClick={onClose} className="w-full text-xs text-slate-400 hover:text-white font-tech uppercase tracking-wider" data-testid="modal-done-btn">
        Done
      </button>
    </div>
  );
}

export function GetStartedModal({ open, onClose, services, initialService, mode, transcript, currentUser }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', service_id: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [existingTicket, setExistingTicket] = useState(null);

  useEffect(() => {
    if (!open) return;
    const userText = (transcript || []).filter((t) => t.role === 'user').map((t) => t.content).join('\n');
    setForm({
      name: currentUser?.name || '',
      email: currentUser?.email || '',
      service_id: initialService?.service_id || '',
      description: userText,
    });
    setError('');
    setExistingTicket(null);
  }, [open, initialService, transcript, currentUser]);

  const selected = services.find((s) => s.service_id === form.service_id);
  const subscribe = mode === 'subscribe';
  const consult = isConsultService(selected) && !subscribe;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await apiFetch(`${API}/guest/start`, {
        method: 'POST',
        silent: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          title: form.description.split('\n')[0].slice(0, 80) || undefined,
          transcript: (transcript || []).slice(-30),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(errorText(data));
      if (data.token) setStoredToken(data.token);
      if (data.account_status === 'existing') return setExistingTicket(data.ticket_id);
      if (subscribe && (await startSubscription(data.ticket_id))) return;
      navigate(`/tickets/${data.ticket_id}`);
    } catch (err) {
      console.error('Guest start error:', err);
      setError('Could not reach the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  let title = 'Get help now';
  if (subscribe) title = 'Start your managed plan';
  else if (consult) title = 'Request a free consultation';
  let submitLabel = 'Send & open my ticket';
  if (subscribe) submitLabel = 'Continue to secure checkout';
  else if (consult) submitLabel = 'Request consultation';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none border border-[#00f0ff]/50 bg-[#0d0d2b] text-white max-w-lg nw-landing" data-testid="service-request-modal">
        <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-tight">{title}</DialogTitle>
        <DialogDescription className="text-slate-400 text-sm">
          No account or password needed — just your name and email so we can reply.
        </DialogDescription>
        {existingTicket ? <ExistingAccount ticketId={existingTicket} onClose={onClose} /> : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} htmlFor="gs-name">Name</label>
                <input id="gs-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls} placeholder="Jane Doe" data-testid="modal-input-name" />
              </div>
              <div>
                <label className={labelCls} htmlFor="gs-email">Email</label>
                <input id="gs-email" type="email" required value={form.email} disabled={!!currentUser}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={`${inputCls} disabled:opacity-60`} placeholder="jane@email.com" data-testid="modal-input-email" />
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="gs-service">Service</label>
              <select id="gs-service" required value={form.service_id} disabled={subscribe}
                onChange={(e) => setForm({ ...form, service_id: e.target.value })}
                className={`${inputCls} disabled:opacity-60`} data-testid="modal-select-service">
                <option value="">Not sure? Pick the closest…</option>
                {GROUPS.map(([cat, label]) => (
                  <optgroup key={cat} label={label}>
                    {services.filter((s) => s.category === cat).map((s) => (
                      <option key={s.service_id} value={s.service_id}>{`${s.name} — ${priceText(s)}`}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {selected?.diagnostic_fee_applies && (
                <p className="mt-2 text-xs text-[#ff00ff] font-tech" data-testid="modal-diagnostic-note">
                  $75 diagnostic first, credited toward the {priceText(selected)} total.
                </p>
              )}
            </div>
            <div>
              <label className={labelCls} htmlFor="gs-desc">{consult ? 'Tell us about your project' : 'What do you need help with?'}</label>
              <textarea id="gs-desc" required rows={4} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className={inputCls} placeholder={consult ? 'Business, goals, timeline…' : 'What happened, on which device…'}
                data-testid="modal-input-notes" />
            </div>
            {error && <p className="text-sm text-red-400 border border-red-500/50 bg-red-500/10 px-3 py-2" data-testid="modal-error">{error}</p>}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} data-testid="modal-close-btn"
                className="nw-btn px-4 py-3 border border-slate-600 text-slate-300 font-tech uppercase tracking-wider text-xs">Cancel</button>
              <button type="submit" disabled={submitting} data-testid="modal-submit-request-btn"
                className="nw-btn flex-1 flex items-center justify-center gap-2 py-3 bg-[#ff00ff] text-[#0a0a1f] hover:bg-white font-tech uppercase tracking-wider text-sm disabled:opacity-50">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {submitLabel}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
